import { createApp } from "vue";
import { createVuetify } from "vuetify";
import { setupI18n } from "./plugins/i18n";
import { useSettings } from "./store/settings";
import { useUI } from "./store/ui";
import { useUser } from "./store/user";
import { useContext } from "./store/context";
import { useRole } from "./store/role";
// import { useContactTypes } from "./store/contactTypes";
import { useStatus } from "./store/status";
import { useConfirmDialogStore } from "./store/confirmDialogStore";
import { useLocale } from "./store/locale";
import { useConfig } from "@/store/config";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import "@/assets/css/index.css";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { DEFAULT_VARIANT, VARIANT_REGISTRY } from "@/variants";
import { debounce } from "lodash";
import * as Sentry from "@sentry/vue";
import { runConfig } from "@/config/runtime-config";
import { SessionStorage } from "./storage";
import { instance } from "./api";
import noAutocomplete from "./directives/noAutocomplete";
import callablePhone from "./directives/callablePhone/callablePhone";
import vSplitFlap from "./directives/vSplitFlap/vSplitFlap";
import { variantDirective } from "./directives/variantDirective";
import { keycloak, loadToken, refreshToken } from "@/utilities/keycloak";
import type { KeycloakOnLoad } from "keycloak-js";
import { initFeatureFlagDebug } from "@/utilities/feature-flag-debug";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import type { Role } from "@/types/role";
import { useNetwork } from "./store/network";
import { useScope } from "./store/scope";
import { useOpportunityNetworkRoleRules } from "./store/opportunityNetworkRoleRules";
import { ACTIVE_ROLE_QUERY_PARAM } from "@/utilities/functions/activeRoleNavigation";
import {
  isLoginRequiredError,
  renderBootstrapFailure,
  reportBootstrapError,
} from "@/utils/bootstrapError";
import { getUserContext } from "./api/apiclient/user";

const variantThemes = Object.fromEntries(
  Object.values(VARIANT_REGISTRY).map((variant) => [
    variant.key,
    { dark: false, colors: variant.themeColors },
  ])
);

const vuetify = createVuetify({
  components,
  directives,
  theme: {
    // Tema della variante di default; al boot viene selezionato quello della variante risolta
    defaultTheme: DEFAULT_VARIANT.key,
    themes: {
      ...variantThemes,
      // Alias storico: stessi colori della variante di default, per eventuali riferimenti residui
      hdTheme: { dark: false, colors: DEFAULT_VARIANT.themeColors },
    },
  },
});

const keycloakParams = {
  onLoad: "check-sso" as KeycloakOnLoad,
  // Verifica la sessione SSO in un iframe nascosto (public/silent-check-sso.html)
  // invece di un redirect top-level dell'intera finestra. Senza questo, ogni
  // cold-load — inclusa la window popup /sip-bar — naviga via verso l'IDP e
  // ritorna, facendo "sparire" la popup prima che la barra SIP monti.
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  checkLoginIframe: false,
  enableLogging: false,
};

const app = createApp(App);

// Feature Flags Debug Bridge
initFeatureFlagDebug();

// Plugin base
setupI18n(app);
app.use(store);
app.directive("no-autocomplete", noAutocomplete);
app.directive("v-split-flap", vSplitFlap);
app.directive("callable-phone", callablePhone);
app.directive("variant", variantDirective);

// Carica token iniziale
loadToken();

/**
 * Ultima linea di difesa del boot: segnala l'errore su console e monitoraggio e
 * disegna la schermata di fallimento al posto della pagina bianca. Qui l'app Vue
 * non è montata, quindi la schermata è DOM puro (vedi renderBootstrapFailure).
 */
function failBootstrap(stage: string, error: unknown): void {
  reportBootstrapError(stage, error);

  const container = document.getElementById("app");
  if (!container) return;

  renderBootstrapFailure({ container, error, reload: () => window.location.reload() });
}

function setupBreakpointListener() {
  const UI = useUI();
  const onResize = debounce(UI.getBreakpoint, 500);
  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
  UI.getBreakpoint(); // Inizializza subito
}

function getActiveRoleIdFromQuery(): number | null {
  const queryRole = new URLSearchParams(window.location.search).get(ACTIVE_ROLE_QUERY_PARAM);
  if (!queryRole) return null;

  const parsedRoleId = Number.parseInt(queryRole, 10);
  return Number.isNaN(parsedRoleId) ? null : parsedRoleId;
}

async function loadUserContext() {
  const userStore = useUser();
  const contextStore = useContext();
  const roleStore = useRole();
  // const contactTypesStore = useContactTypes();
  const statusStore = useStatus();
  const confirmStore = useConfirmDialogStore();
  const networkStore = useNetwork();
  const scopeStore = useScope();

  // Reset dialogo conferma
  confirmStore.close();

  if (!userStore.isLoggedIn) return;

  try {
    userStore.setTokens(keycloak);
    await Promise.all([
      userStore.getCurrent(), // Profilo
      userStore.getRoles(), // Ruoli
    ]);

    const roleIdFromQuery = getActiveRoleIdFromQuery();
    const canUseRoleFromQuery =
      roleIdFromQuery !== null &&
      userStore.roles.some(
        (role) => role.role_id === roleIdFromQuery && role.status === "Active" && !role.isSystemRole
      );

    if (canUseRoleFromQuery && roleIdFromQuery !== null) {
      await userStore.updateSavedRole(roleIdFromQuery, true);
    }

    await getUserContext({
      application: 'GAIA'
    });

    // Caricamento parallelo dati
    await Promise.all([
      roleStore.loadList(),
      contextStore.loadApplicationList(),
      statusStore.loadInvitationStatusEvolutions(), // RIMANE
      networkStore.init(),
      // contactTypesStore.loadContactTypesList(),
      // leadStore.setup(),
      // notificationStore.setupAuthWatcher(),
      scopeStore.setup(),
      // dynamicTableConfigStore.setup(),
      // actionStore.setupActionsTab(),
    ]);

    // Dipende da scopeStore: deve girare dopo il setup degli scope per usare gli scopeIds visibili
    const opportunityNetworkRoleRulesStore = useOpportunityNetworkRoleRules();
    await opportunityNetworkRoleRulesStore.setup(scopeStore.getScopeListStringified);

    // Filtra app disponibili: ruoli attivi escluso finaluser (role_id: 1), una sola voce per applicazione
    const appIdsSeen = new Set<number>();
    contextStore.availableApplications = userStore.allRoles
      .filter((role: Role) => role.status === "Active")
      .map((role) => contextStore.list.find((app) => app.id === role.application_id))
      .filter((app): app is NonNullable<typeof app> => !!app && !!app.uris?.length)
      .filter((app) => {
        if (appIdsSeen.has(app.id)) return false;
        appIdsSeen.add(app.id);
        return true;
      });
  } catch (error) {
    reportBootstrapError("loadUserContext", error);
  }
}

async function setupLocalization() {
  const localeStore = useLocale();
  // await localeStore.init();
  const locale = SessionStorage.getOr(["locale"], "it-IT");
  await localeStore.changeLanguage(locale);
  localeStore.fetchLanguages(); // async
}

function setupConfigurationInit() {
  const configStore = useConfig();
  configStore.init();
}

function setupMonitoring() {
  if (!runConfig.SENTRY_ENABLED) return;

  Sentry.init({
    app,
    dsn: "https://0d322e0910b7218fe850a9c7cab531eb@o4508642628796416.ingest.de.sentry.io/4508969329819728",
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    replaysSessionSampleRate: import.meta.env.MODE === "development" ? 1.0 : 0.1,
    replaysOnErrorSampleRate: import.meta.env.MODE === "development" ? 0.1 : 1.0,
    integrations: [
      Sentry.replayIntegration({
        // maskAllText: true,
        // blockAllMedia: true,
      }),
    ],
  });
}

try {
  keycloak
    .init(keycloakParams)
    .then((authenticated) => {
      if (authenticated) {
        refreshToken(); // Avvia il refresh automatico del token
      }

      // Override da URL: se host contiene eniplenitude.gaia → application ID 31 (GAIAENI) per tutto il portale
      const settingsStore = useSettings();
      settingsStore.setUrlOverrideFromCurrentUrl();
      instance.defaults.headers["X-Application"] = settingsStore.effectivePlatformIdHeader;
      instance.defaults.headers["X-FrontendId"] = settingsStore.platformFrontendId;

      // Tema della variante risolta (oggi identico tra varianti: switch invisibile)
      vuetify.theme.global.name.value = settingsStore.effectiveVariantConfig.key;

      // Abilita gestione layout
      setupBreakpointListener();

      // Flag di inizializzazione
      settingsStore.authInitialized = true;
    })
    .then(async () => {
      setupMonitoring();

      // Monta plugin finali
      app.use(vuetify);

      // Carica il contesto utente PRIMA di montare l'app
      // Questo assicura che i token e lo stato utente siano pronti per le chiamate API
      await loadUserContext();

      // Le traduzioni sono un degrado accettabile: un 4xx del servizio lingue
      // (es. X-Application non riconosciuto → 400) non deve impedire il mount.
      try {
        await setupLocalization();
      } catch (error) {
        reportBootstrapError("setupLocalization", error);
      }

      setupConfigurationInit();
      app.use(router);

      // Monta l'app solo quando tutto è pronto
      app.mount("#app");
      console.log("[App] Mounted successfully");
    })
    .catch((error) => {
      if (isLoginRequiredError(error)) {
        return keycloak.login();
      }

      failBootstrap("bootstrap", error);
    });
} catch (error) {
  failBootstrap("keycloak.init", error);
}
